/* External */
import { useMemo, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';

/* Ott-common */
import { Tracks } from '@dtvgo/player-utils';

/* Components */
import TrackItem from './TrackItem';
import ScrollView, { ScrollPosition } from '../../../ScrollView';
import ScrollIntoViewIfFocused from '../../../ScrollIntoViewIfFocused';

/* Utils */
import { useIsBrazil } from '../../../../providers/ConfigProvider';
import { noOp } from '../../../../utils/functions';
import { useOnBackPress } from '../../../../utils/keyDownEvents';
import { Direction } from '../../../../utils/spatialNavigation';
import { AUDIO_LANGUAGES_ORDER, CLOSED_CAPTIONS } from './utils';

/* Styles */
import './TracksControls.scss';

function TracksControls({
  audioTracks,
  subtitleTracks = [],
  show = false,
  selectAudioTrack = noOp,
  selectSubtitleTrack = noOp,
  disableSubtitles = noOp,
  hide = noOp,
}) {
  const isBrazil = useIsBrazil();

  useOnBackPress({ listener: hide, when: show });

  const handleArrowLeft = useCallback((direction) => {
    if (direction === Direction.LEFT) {
      hide();
      return false;
    }
    return true;
  }, [hide]);

  const orderByIso = isBrazil ? AUDIO_LANGUAGES_ORDER.BR : AUDIO_LANGUAGES_ORDER.SSLA;
  const sortAudioTracks = useMemo(() => Tracks.sortAudioTracks(orderByIso), [orderByIso]);
  const sortTextTracks = useMemo(() => Tracks.sortTextTracks(orderByIso), [orderByIso]);

  const audioTracksFormatted = useMemo(() => sortAudioTracks(audioTracks)?.map((track, index) => {
    const translationId = track.isDescriptive
      ? `tracks.${track.iso}.descriptive`
      : `tracks.${track.iso}`;
    return (
      <ScrollIntoViewIfFocused
        key={track.id}
        position={ScrollPosition.CENTER}
      >
        <TrackItem
          track={track}
          index={index}
          isSelected={track.isSelected}
          onEnterPress={selectAudioTrack}
          onArrowPress={handleArrowLeft}
          shouldStealFocus={show && index === 0}
        >
          <FormattedMessage
            id={Tracks.isLanguageUnknown(track) ? 'tracks.audio.unknown' : translationId}
            values={{ index: track.unknownIndex }}
          />
          {track.isDolby && (
            <>
              {' '}
              <FormattedMessage id="tracks.dolbyDigital" />
            </>
          )}
        </TrackItem>
      </ScrollIntoViewIfFocused>
    );
  }), [
    audioTracks,
    handleArrowLeft,
    selectAudioTrack,
    sortAudioTracks,
    show,
  ]);

  const subtitleTracksFormatted = useMemo(() => sortTextTracks(subtitleTracks)?.map(
    (track) => (
      <ScrollIntoViewIfFocused
        key={track.id}
        position={ScrollPosition.CENTER}
      >
        <TrackItem
          track={track}
          isSelected={track.isSelected}
          onEnterPress={selectSubtitleTrack}
          onArrowPress={handleArrowLeft}
        >
          <FormattedMessage
            id={Tracks.isLanguageUnknown(track) ? 'tracks.text.unknown' : `tracks.${track.iso}`}
            values={{ index: track.unknownIndex }}
          />
          {track.isClosedCaption ? CLOSED_CAPTIONS : ''}
        </TrackItem>
      </ScrollIntoViewIfFocused>
    ),
  ), [handleArrowLeft, selectSubtitleTrack, sortTextTracks, subtitleTracks]);

  const isSelectDisabledSubtitle = useMemo(
    () => !subtitleTracks.some(({ isSelected }) => isSelected),
    [subtitleTracks],
  );

  return (
    <ScrollView className="dtv-html5-c-tracks-controls">
      <div className="dtv-html5-c-tracks-controls__tracks-content">
        <div className="dtv-html5-c-tracks-controls__audio-track-content">
          <h2 className="dtv-html5-c-tracks-controls__title">
            <FormattedMessage id="common.audioTrack" />
          </h2>
          <ul role="menu">
            {audioTracksFormatted}
          </ul>
        </div>
        <div className="dtv-html5-c-tracks-controls__subtitle-content">
          <h2 className="dtv-html5-c-tracks-controls__title">
            <FormattedMessage id="common.subtitles" />
          </h2>
          <ul role="menu">
            <ScrollIntoViewIfFocused position={ScrollPosition.CENTER}>
              <TrackItem
                isSelected={isSelectDisabledSubtitle}
                onEnterPress={disableSubtitles}
                onArrowPress={handleArrowLeft}
              >
                <FormattedMessage id="common.disabled" />
              </TrackItem>
            </ScrollIntoViewIfFocused>
            {subtitleTracksFormatted}
          </ul>
        </div>
      </div>
    </ScrollView>
  );
}

TracksControls.propTypes = {
  audioTracks: PropTypes.instanceOf(Array).isRequired,
  subtitleTracks: PropTypes.instanceOf(Array),
  show: PropTypes.bool,
  selectAudioTrack: PropTypes.func,
  selectSubtitleTrack: PropTypes.func,
  disableSubtitles: PropTypes.func,
  hide: PropTypes.func,
};

export default memo(withFocusable()(memo(TracksControls)));
